import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { StatusBadge } from '../components/Badges';
import { Plus, FileCheck, Upload, FileText, CheckCircle2, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['Equipment Finance', 'Business Loans', 'Asset Finance', 'Personal Loans', 'Mortgage', 'Solar', 'Insurance', 'Auto Finance'];
const SOURCES = ['Google Search', 'Facebook Ads', 'Google Search + Landing Page', 'LinkedIn', 'TikTok', 'Organic SEO', 'Email Campaign'];
const REGIONS = ['Australia-wide', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

const APPROVAL_CONFIG = {
  pending: { color: 'bg-amber-50 text-amber-800 border-amber-200', icon: Clock, label: 'Pending Review' },
  approved: { color: 'bg-green-50 text-green-800 border-green-200', icon: CheckCircle2, label: 'Approved' },
  rejected: { color: 'bg-red-50 text-red-800 border-red-200', icon: X, label: 'Rejected' },
};

export default function SellerSourcesPage() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedPath, setUploadedPath] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await api.get('/sources/my');
      setSources(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/leads/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedPath(res.data.file_path);
      setUploadedName(res.data.original_filename);
      toast.success('Proof uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sources', {
        name,
        source_type: sourceType,
        category,
        region,
        description,
        consent_proof_path: uploadedPath || null
      });
      toast.success('Source submitted for review');
      setOpen(false);
      setName(''); setSourceType(''); setCategory(''); setRegion('');
      setDescription(''); setUploadedPath(''); setUploadedName('');
      fetchSources();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono mb-1">
              Source Profiles
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">
              Approved Lead Sources
            </h1>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Register your lead generation funnels. Once approved, leads from these sources can be listed faster and at premium pricing.
            </p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="add-source-btn">
                <Plus className="w-4 h-4 mr-2" /> Register Source
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Register a Lead Source</DialogTitle>
                <DialogDescription>
                  LeadXchange admins review every source. Approved sources become an asset on your alias.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Source Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. AU Equipment Finance Funnel" required data-testid="source-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Source Type</Label>
                    <Select value={sourceType} onValueChange={setSourceType} required>
                      <SelectTrigger data-testid="source-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Select value={region} onValueChange={setRegion} required>
                      <SelectTrigger data-testid="source-region-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory} required>
                    <SelectTrigger data-testid="source-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="How leads are generated, consent flow, screening rules..." required data-testid="source-description-input" />
                </div>
                <div>
                  <Label>Consent Proof (Optional)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm">{uploading ? 'Uploading...' : 'Upload File'}</span>
                      <input type="file" className="hidden" onChange={handleFile} disabled={uploading} data-testid="source-proof-upload" />
                    </label>
                    {uploadedName && (
                      <span className="text-xs text-gray-600 flex items-center gap-1">
                        <FileText className="w-3 h-3" />{uploadedName}
                      </span>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="submit-source-btn">
                    Submit for Review
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {sources.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm flex flex-col items-center gap-2">
            <FileCheck className="w-8 h-8 text-gray-300" />
            No sources registered yet. Register your first one to unlock premium listing.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sources.map(src => {
              const cfg = APPROVAL_CONFIG[src.approval_status] || APPROVAL_CONFIG.pending;
              const Icon = cfg.icon;
              return (
                <div key={src.source_id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`source-${src.source_id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-mono text-xs text-gray-400">{src.source_id}</div>
                      <h3 className="font-heading text-base font-semibold mt-1">{src.name}</h3>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 border rounded ${cfg.color}`}>
                      <Icon className="w-3 h-3" />
                      {cfg.label}
                    </span>
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{src.source_type}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Category</span><span className="font-medium">{src.category}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Region</span><span className="font-medium">{src.region}</span></div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 line-clamp-3">
                    {src.description}
                  </div>
                  {src.review_note && (
                    <div className="mt-3 bg-gray-50 border border-gray-200 rounded p-2 text-xs">
                      <span className="font-mono uppercase tracking-wider text-gray-500">Admin Note:</span>{' '}
                      <span className="text-gray-800">{src.review_note}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
